/**
 * Reminders to export realms while there's no sync (docs/SYNC-PLAN.md step 0). A realm
 * with entries that changed since it was last exported here, more than 14 days ago (or
 * never), gets a reminder, which can be put off for 14 days. Kept in this browser's local
 * storage: an export is itself per device, and losing these only brings a reminder back.
 */

export const REMIND_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

const exportedKey = (realmId: string) => `realmery.exported.${realmId}`;
const snoozedKey = (realmId: string) => `realmery.backup-snoozed.${realmId}`;
const SAFARI_NOTE_KEY = 'realmery.safari-note-dismissed';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the reminder simply comes back.
  }
}

const time = (value: string | null) => (value ? Date.parse(value) : NaN);

export interface BackupState {
  /** When this realm was last exported in this browser, if ever. */
  readonly exportedAt: string | null;
  readonly due: boolean;
}

/** Whether `realm` should be reminded about, by the rule above. Pure, for tests. */
export function backupDue(
  realm: { readonly modified: string; readonly entryCount: number },
  exportedAt: string | null,
  snoozedUntil: string | null,
  now: number,
): boolean {
  if (realm.entryCount === 0) return false;
  const exported = time(exportedAt);
  if (Number.isFinite(exported) && time(realm.modified) <= exported) return false;
  if (Number.isFinite(exported) && now - exported < REMIND_AFTER_MS) return false;
  const snoozed = time(snoozedUntil);
  return !(Number.isFinite(snoozed) && now < snoozed);
}

export function backupState(realm: { readonly file: string; readonly modified: string; readonly entryCount: number }, realmId: string, now = Date.now()): BackupState {
  const exportedAt = read(exportedKey(realmId));
  return { exportedAt, due: backupDue(realm, exportedAt, read(snoozedKey(realmId)), now) };
}

export function recordExport(realmId: string, now = new Date()): void {
  write(exportedKey(realmId), now.toISOString());
}

export function snoozeReminder(realmId: string, now = Date.now()): void {
  write(snoozedKey(realmId), new Date(now + REMIND_AFTER_MS).toISOString());
}

/** Safari on an iPhone or iPad, not opened from the Home Screen, which is when its storage limits apply. */
export function safariMayClearStorage(nav: Navigator = navigator, matches = (q: string) => matchMedia(q).matches): boolean {
  const ios = /iP(hone|ad|od)/.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  const safari = /Safari\//.test(nav.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(nav.userAgent);
  const standalone = matches('(display-mode: standalone)') || (nav as Navigator & { standalone?: boolean }).standalone === true;
  return ios && safari && !standalone;
}

export const safariNoteDismissed = () => read(SAFARI_NOTE_KEY) !== null;
export const dismissSafariNote = () => write(SAFARI_NOTE_KEY, new Date().toISOString());
